import { Modal as AntdModal, ModalProps as AntdModalProps } from 'antd';
import classNames from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';

import './modal.scss';

type Props = AntdModalProps & {
  onOk?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCancel?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  defaultOpen?: boolean;
  forceOk?: boolean;
};

export type ModalHandle = {
  open: () => void;
  close: () => void;
  cancel: () => void;
  ok: () => void;
  isOpen: boolean;
};

export const Modal = forwardRef<ModalHandle, Props>(
  (
    {
      onCancel,
      onOk,
      defaultOpen,
      forceOk,
      footer,
      open,
      closable,
      maskClosable,
      cancelButtonProps,
      width,
      className,
      ...antdProps
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpen || false);

    const handleOk = useCallback(
      (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
          onOk?.(e);
          setIsOpen(false);
        } catch (e) {
          console.error('Error in Modal onOk handler:', e);
        }
      },
      [onOk],
    );

    const handleCancel = useCallback(
      (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
          if (forceOk) {
            console.warn('Modal cannot be closed because forceOk is enabled.');
            return;
          }
          onCancel?.(e);
          setIsOpen(false);
        } catch (e) {
          console.error('Error in Modal onCancel handler:', e);
        }
      },
      [onCancel],
    );

    useImperativeHandle(
      ref,
      (): ModalHandle => ({
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        cancel: () => handleCancel(),
        ok: () => handleOk(),
        isOpen, // Can't trigger rerender but can be used to check if modal is open
      }),
      [handleCancel, handleOk, isOpen],
    );

    return (
      <AntdModal
        className={classNames('the5ers-modal', className)}
        open={open ?? isOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        closable={closable ?? !forceOk}
        maskClosable={maskClosable ?? !forceOk}
        cancelButtonProps={{
          hidden: forceOk,
          ...cancelButtonProps,
        }}
        footer={footer !== null && footer}
        width={width ?? 'fit-content'}
        {...antdProps}
      ></AntdModal>
    );
  },
);
